import { ReactNode } from 'react';
import { Box, Dialog, DialogContent, Divider, Grow, IconButton, Stack, Typography } from '@mui/material';
import { alpha, SxProps, Theme, useTheme } from '@mui/material/styles';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { DISPLAY_FONT, RADIUS, REDUCED_MOTION } from 'components/ui/tokens';
import { focusRingSx } from 'components/ui/styleKit';
import { Eyebrow, ink } from 'sections/mcp/SignalDesk';
import { EASE, fadeSwap } from 'sections/rooms/rooms.motion';
import { spring, useTourMotion } from 'sections/welcome-tour/motion';

const TRANSITION_MS = 220;

interface FlowDialogProps {
  open: boolean;
  onClose: () => void;
  /** Glyph shown in the header badge; sized by the caller. */
  icon: ReactNode;
  eyebrow: string;
  title: string;
  /** id for aria-labelledby; unique per dialog. */
  titleId: string;
  closeLabel: string;
  /** Paper max width in px; changing it eases the dialog between widths. */
  maxWidth: number;
  /** 0-1: how far through the flow the user is; drawn as the header's progress line. */
  progress: number;
  /** Indeterminate shimmer on the progress line while work is in flight. */
  busy?: boolean;
  footer?: ReactNode;
  /** Root overrides, e.g. zIndex when opened above a raised drawer. */
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/** Multi-step modal frame: atmosphere band, animated title, progress line, width that eases between steps. */
const FlowDialog = ({
  open,
  onClose,
  icon,
  eyebrow,
  title,
  titleId,
  closeLabel,
  maxWidth,
  progress,
  busy,
  footer,
  sx,
  children
}: FlowDialogProps) => {
  const theme = useTheme();
  const { reduce, v } = useTourMotion();
  const primary = theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      TransitionComponent={Grow}
      transitionDuration={reduce ? 0 : TRANSITION_MS}
      aria-labelledby={titleId}
      sx={sx}
      PaperProps={{
        sx: {
          maxWidth,
          borderRadius: RADIUS.panel,
          backgroundImage: 'none',
          overflow: 'hidden',
          transition: 'max-width .5s cubic-bezier(.2,.7,.3,1)',
          [REDUCED_MOTION]: { transition: 'none' }
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          backgroundImage: `radial-gradient(520px 170px at 12% -60px, ${alpha(primary, dark ? 0.22 : 0.12)}, transparent 70%)`
        }}
      >
        <Box
          component={motion.div}
          initial={reduce ? false : { scale: 0.6, rotate: -12, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={spring}
          sx={{
            width: 42,
            height: 42,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: RADIUS.control,
            color: ink(theme),
            backgroundColor: alpha(primary, dark ? 0.16 : 0.1),
            border: `1px solid ${alpha(primary, 0.3)}`,
            boxShadow: `0 8px 22px -12px ${alpha(primary, 0.8)}`
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Eyebrow>{eyebrow}</Eyebrow>
          <AnimatePresence exitBeforeEnter initial={false}>
            <Typography
              key={title}
              id={titleId}
              component={motion.h2}
              variants={v(fadeSwap)}
              initial="hidden"
              animate="show"
              exit="exit"
              sx={{
                m: 0,
                fontFamily: DISPLAY_FONT,
                fontSize: { xs: 19, sm: 21 },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              {title}
            </Typography>
          </AnimatePresence>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label={closeLabel} sx={focusRingSx(theme)}>
          <CloseOutlined />
        </IconButton>
      </Stack>

      <Box sx={{ position: 'relative', height: 2, backgroundColor: theme.palette.divider, overflow: 'hidden' }}>
        <Box
          component={motion.div}
          initial={false}
          animate={{ scaleX: progress }}
          transition={{ duration: reduce ? 0 : 0.7, ease: EASE }}
          sx={{
            position: 'absolute',
            inset: 0,
            transformOrigin: 'left',
            background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${primary}, ${theme.palette.primary.light})`
          }}
        />
        {busy && !reduce && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              '@keyframes flowProgressSweep': { from: { transform: 'translateX(-100%)' }, to: { transform: 'translateX(100%)' } },
              background: `linear-gradient(90deg, transparent, ${alpha('#fff', 0.7)}, transparent)`,
              animation: 'flowProgressSweep 1.1s ease-in-out infinite'
            }}
          />
        )}
      </Box>

      <DialogContent sx={{ px: 3, py: 2.75 }}>{children}</DialogContent>
      {footer && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2 }}>{footer}</Box>
        </>
      )}
    </Dialog>
  );
};

export default FlowDialog;
